import { render as toPlainText } from "datocms-structured-text-to-plain-text"
import { TypesafeStructuredText } from "datocms-structured-text-utils"
import type { ReactElement, ReactNode } from "react"
import React from "react"

import { isString } from "./types"

function isStructuredText(content: unknown): content is TypesafeStructuredText {
  return (
    typeof content === "object" &&
    content !== null &&
    "value" in content &&
    typeof (content as { value: unknown }).value === "object"
  )
}

function extractTextFromStructuredText(
  structuredText: TypesafeStructuredText,
): string {
  return toPlainText(structuredText) || ""
}

export function extractTextFromReactElement(node: ReactNode): string {
  if (!node) return ""

  if (isString(node) || typeof node === "number") {
    return String(node)
  }

  if (Array.isArray(node)) {
    return node
      .map(extractTextFromReactElement)
      .filter(text => text.length > 0)
      .join(" ")
  }

  if (React.isValidElement(node)) {
    const element = node as ReactElement

    if (element.type === "br") {
      return " "
    }

    if (element.type === "li") {
      const text = extractTextFromReactElement(element.props.children)
      return text ? `• ${text}` : ""
    }

    if (element.props && element.props.children) {
      return extractTextFromReactElement(element.props.children)
    }
  }

  return ""
}

export function cleanExtractedText(text: string): string {
  return text
    .replace(/\s+/g, " ") // Replace multiple whitespace with single space
    .replace(/\s*•\s*/g, " • ") // Normalize bullet points
    .trim()
}

export function extractText(content: unknown): string {
  if (!content) return ""

  if (isString(content) || typeof content === "number") {
    return String(content)
  }

  if (isStructuredText(content)) {
    return extractTextFromStructuredText(content)
  }

  if (React.isValidElement(content) || Array.isArray(content)) {
    return cleanExtractedText(extractTextFromReactElement(content))
  }

  if (typeof content === "object" && content !== null && "body" in content) {
    const bodyContent = (content as { body: unknown }).body
    if (isStructuredText(bodyContent)) {
      return extractTextFromStructuredText(bodyContent)
    }
  }

  return ""
}
